import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import NewsletterSignupForm from '../components/NewsletterSignupForm';
import SEOHead from '../components/SEOHead';
import { trackContactFormSubmit, getRadioSource, trackRadioConversion, clearRadioSource, trackAdsConversion } from '../utils/analytics';
import { getStoredUTMParams } from '../utils/utmTracking';

const API_URL = "";

// How did you hear about us options - with specific radio stations for attribution
const referralSources = [
  { value: 'kahl_radio', label: 'KAHL Radio' },
  { value: 'kpac_radio', label: 'KPAC Radio' },
  { value: 'google', label: 'Google Search' },
  { value: 'social', label: 'Social Media (Facebook, Instagram)' },
  { value: 'friend', label: 'Friend / Word of Mouth' },
  { value: 'website', label: 'Website / Search' },
  { value: 'other', label: 'Other' }
];

// Default academy info for initial render
const defaultAcademyInfo = {
  name: "The Coppini Academy of Fine Art",
  contact: {
    address: "115 Melrose Place, San Antonio, TX 78212",
    phone: "(210) 530-8445",
    email: "info@thecoppini.org"
  }
};

export const Contact = () => {
  const [academyInfo, setAcademyInfo] = useState(defaultAcademyInfo);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    referralSource: '',
    referralOther: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/academy-info`);
        if (res.ok) {
          setAcademyInfo(await res.json());
        }
      } catch (err) {
        console.error('Error fetching academy info:', err);
      }
    };
    fetchInfo();
    
    // Auto-select KPAC radio if visitor came from /welcome page
    // (KAHL listeners come directly to main site - they self-report via dropdown)
    const radioSource = getRadioSource();
    if (radioSource && radioSource.source.includes('kpac')) {
      setFormData(prev => ({ ...prev, referralSource: 'kpac_radio' }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate referral source
    if (!formData.referralSource) {
      toast.error('Please select how you heard about us');
      return;
    }
    
    if (formData.referralSource === 'other' && !formData.referralOther.trim()) {
      toast.error('Please specify where you heard about us');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get UTM data if available (captured from URL params)
      const utmData = getStoredUTMParams();
      
      // Check for radio attribution
      const radioSource = getRadioSource();
      
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          referral_source: formData.referralSource,
          referral_other: formData.referralOther || null,
          utm_data: utmData,
          radio_attribution: radioSource ? {
            source: radioSource.source,
            landing_page: radioSource.landingPage,
            landing_timestamp: radioSource.timestamp
          } : null
        })
      });
      
      if (response.ok) {
        // Track contact form submission
        trackContactFormSubmit(formData.subject);
        trackAdsConversion();
        
        // Track radio conversion if applicable
        if (radioSource) {
          trackRadioConversion('contact_form', 10, {
            subject: formData.subject,
            referral_selected: formData.referralSource
          });
          clearRadioSource(); // Clear after attribution
        }
        
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you as soon as possible.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          referralSource: '',
          referralOther: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or email us directly at info@thecoppini.org'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReferralChange = (value) => {
    setFormData({ ...formData, referralSource: value, referralOther: value !== 'other' ? '' : formData.referralOther });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact Us"
        description="Contact The Coppini Academy of Fine Art at 115 Melrose Place, San Antonio, TX 78212. Phone, email, directions, and hours of operation."
        canonicalPath="/contact"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-stone-900 mb-6">Contact Us</h1>
          <p className="text-xl text-stone-600">
            Get in touch with us - we'd love to hear from you
          </p>
        </div>
      </section>


      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-serif text-stone-900 mb-6">Send Us a Message</h2>
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <Label htmlFor="name" className="text-stone-700">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-stone-700">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-stone-700">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="What is this regarding?"
                      />
                    </div>

                    <div>
                      <Label htmlFor="referralSource" className="text-stone-700">How did you hear about us? *</Label>
                      <Select 
                        value={formData.referralSource} 
                        onValueChange={handleReferralChange}
                        required
                      >
                        <SelectTrigger className="mt-2" id="referralSource">
                          <SelectValue placeholder="Please select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {referralSources.map((source) => (
                            <SelectItem key={source.value} value={source.value}>
                              {source.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.referralSource === 'other' && (
                      <div>
                        <Label htmlFor="referralOther" className="text-stone-700">Please tell us where you heard about us *</Label>
                        <Input
                          id="referralOther"
                          name="referralOther"
                          value={formData.referralOther}
                          onChange={handleChange}
                          required
                          className="mt-2"
                          placeholder="Please specify..."
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="message" className="text-stone-700">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="mt-2"
                        placeholder="Your message..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-serif text-stone-900 mb-6">Contact Information</h2>
              
              {/* Google Maps Embed */}
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg border border-stone-200" data-testid="google-map">
                <iframe
                  src="https://maps.google.com/maps?q=115+Melrose+Place,+San+Antonio,+TX+78212&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Coppini Academy of Fine Art Location"
                ></iframe>
                <p className="text-center text-stone-500 text-sm py-3 bg-stone-50 italic">
                  Look for us right behind Don & Ben's Liquor!
                </p>
              </div>
              
              <div className="space-y-6 mb-10">
                <Card className="border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cafa-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-cafa-red" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">Address</h3>
                        <p className="text-stone-600">{academyInfo.contact.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cafa-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="text-cafa-red" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">Phone</h3>
                        <p className="text-stone-600">{academyInfo.contact.phone}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cafa-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="text-cafa-red" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-stone-900 mb-1">Hours</h3>
                        <div className="text-stone-600 space-y-1">
                          <p>Monday - Friday: 9:00 AM - 9:30 PM</p>
                          <p>Saturday: 10:00 AM - 4:00 PM</p>
                          <p>Sunday: 3:00 PM - 5:00 PM</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Newsletter Signup */}
              <NewsletterSignupForm className="shadow-sm" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
